import clsx from "clsx";
import { twMerge } from "tailwind-merge";

import {
  Avatar,
  Displayer,
  Skeleton,
  Tooltip,
} from "@smart/itops-sb-design-system-dom";

import { UnpublishedChangesIndicator } from "../../shared/unpublished-changes-indicator";

export const selectedClassNames =
  "bg-cyan-140 relative after:content-[''] after:h-[2rem] after:border-cyan-540 after:absolute after:left-[-0.1rem] after:top-[0.9rem] after:border-r-4 after:rounded-r";

type NavItemProps = {
  section: {
    uri: string;
    values?: {
      title: string;
    };
  };
  index: number;
  currentSectionUri: string;
  setCurrentSectionUri: (uri: string) => void;
  loading: boolean;
  isOver: boolean;
  hasUnpublishedChanges?: boolean;
  isRemoved?: boolean;
};

const NavItem = ({
  section,
  index,
  currentSectionUri,
  setCurrentSectionUri,
  loading,
  isOver,
  hasUnpublishedChanges,
  isRemoved,
}: NavItemProps) => {
  const isCurrent = section.uri === currentSectionUri;

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events
    <div
      className={twMerge(
        clsx(
          "flex items-center gap-medium h-[4rem] px-[1.6rem] py-[0.4rem] rounded border border-transparent",
          !loading &&
            !isRemoved &&
            clsx(
              "hover:bg-cyan-140 cursor-pointer",
              isCurrent && selectedClassNames,
              !isCurrent && isOver && "bg-cyan-140 border-blue-560",
            ),
        ),
      )}
      data-testid={`nav-item-${section.uri}`}
      onClick={() => !isRemoved && setCurrentSectionUri(section.uri)}
    >
      {loading ? (
        <Skeleton />
      ) : (
        <>
          <div className="w-[3.2rem] h-[3.2rem]">
            <Avatar
              text={isRemoved ? "–" : String(index + 1)}
              size={32}
              active={isCurrent}
            />
          </div>
          <Tooltip
            className="overflow-hidden flex-1"
            text={isRemoved ? "This section has been removed" : undefined}
            placement="right"
            offsetBy={10}
            mountTo="body"
          >
            <div
              className={clsx(
                "flex-1 min-w-0 text-ui text-neutral-600 font-semibold overflow-hidden text-ellipsis whitespace-nowrap",
                isCurrent && "text-neutral-850",
                isRemoved && "line-through",
              )}
            >
              <Displayer
                paragraphTag="span"
                value={section.values?.title}
                fallback="Section"
                disableLinks
              />
            </div>
          </Tooltip>
          {hasUnpublishedChanges && !isRemoved && (
            <Tooltip
              text="This section has unpublished changes"
              placement="left"
              offsetBy={7}
              mountTo="body"
            >
              <UnpublishedChangesIndicator dataTestId="section-unpublished-indicator" />
            </Tooltip>
          )}
        </>
      )}
    </div>
  );
};

export { NavItem, NavItemProps };
